import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart3, ArrowRight, TrendingUp, TrendingDown, Scale } from 'lucide-react';
import { Logo } from '../components/Logo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// دالة تنسيق المبالغ بالأرقام الإنجليزية وشعار الريال السعودي الجديد
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '0 ﷼';
  return `${Number(amount).toLocaleString('en-US')} ﷼`;
};

export default function FinancialReports() {
  const navigate = useNavigate();
  const [reportType, setReportType] = useState('daily');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [reportType]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/financial-reports/${reportType}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(response.data);
    } catch (err) {
      console.error('Error fetching report:', err);
    } finally {
      setLoading(false);
    }
  };

  const getReportTitle = () => {
    const titles = {
      daily: 'التقرير اليومي',
      weekly: 'التقرير الأسبوعي',
      monthly: 'التقرير الشهري',
    };
    return titles[reportType] || 'التقرير المالي';
  };

  return (
    <div className="min-h-screen bg-sand" dir="rtl" lang="ar">
      <header className="bg-primary py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
            <ArrowRight className="w-6 h-6 text-white" />
          </button>
          <Logo size="md" />
          <h1 className="text-2xl font-heading font-bold text-white">التقارير المالية</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Type Selector */}
        <div className="mb-8 flex gap-4 justify-center">
          <button
            data-testid="report-daily"
            onClick={() => setReportType('daily')}
            className={`px-6 py-3 rounded-md font-body transition-all ${
              reportType === 'daily'
                ? 'bg-gold text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            يومي
          </button>
          <button
            data-testid="report-weekly"
            onClick={() => setReportType('weekly')}
            className={`px-6 py-3 rounded-md font-body transition-all ${
              reportType === 'weekly'
                ? 'bg-gold text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            أسبوعي
          </button>
          <button
            data-testid="report-monthly"
            onClick={() => setReportType('monthly')}
            className={`px-6 py-3 rounded-md font-body transition-all ${
              reportType === 'monthly'
                ? 'bg-gold text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            شهري
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gold border-t-transparent"></div>
            <p className="mt-4 text-gray-600 font-body">جاري تحميل التقرير...</p>
          </div>
        ) : report ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <TrendingUp className="w-8 h-8 text-green-500" />
                  <span className="text-2xl font-bold text-primary font-heading">
                    {formatCurrency(report.total_receipts)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">إجمالي القبض</p>
                <p className="text-xs text-gray-500 font-body mt-1">
                  {report.receipts_count} سند قبض
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <TrendingDown className="w-8 h-8 text-red-500" />
                  <span className="text-2xl font-bold text-primary font-heading">
                    {formatCurrency(report.total_payments)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">إجمالي الصرف</p>
                <p className="text-xs text-gray-500 font-body mt-1">
                  {report.payments_count} سند صرف
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <BarChart3 className="w-8 h-8 text-gold" />
                  <span className="text-2xl font-bold text-primary font-heading">
                    {formatCurrency(report.total_fees)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">إجمالي الأتعاب</p>
                <p className="text-xs text-gray-500 font-body mt-1">
                  {report.fees_count} سجل أتعاب
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <Scale className="w-8 h-8 text-blue-500" />
                  <span
                    className={`text-2xl font-bold font-heading ${
                      report.net_income >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {formatCurrency(report.net_income)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 font-body">صافي الدخل</p>
              </div>
            </div>

            {/* Detailed Tables */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
              <h3 className="text-xl font-heading font-semibold text-primary mb-4">
                تفاصيل {getReportTitle()}
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-heading font-medium text-primary mb-3">الأتعاب</h4>
                  {report.fees.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-sand">
                          <tr>
                            <th className="px-4 py-2 text-right font-body">العميل</th>
                            <th className="px-4 py-2 text-right font-body">النوع</th>
                            <th className="px-4 py-2 text-right font-body">المبلغ</th>
                            <th className="px-4 py-2 text-right font-body">الحالة</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.fees.map((fee) => (
                            <tr key={fee.id} className="border-b">
                              <td className="px-4 py-2 font-body">{fee.client_name}</td>
                              <td className="px-4 py-2 font-body">{fee.fee_type}</td>
                              <td className="px-4 py-2 font-body font-bold">
                                {formatCurrency(fee.total_amount)}
                              </td>
                              <td className="px-4 py-2 font-body">{fee.payment_status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 font-body">لا توجد أتعاب في هذه الفترة</p>
                  )}
                </div>

                <div>
                  <h4 className="text-lg font-heading font-medium text-primary mb-3">السندات</h4>
                  {report.vouchers.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-sand">
                          <tr>
                            <th className="px-4 py-2 text-right font-body">رقم السند</th>
                            <th className="px-4 py-2 text-right font-body">النوع</th>
                            <th className="px-4 py-2 text-right font-body">المبلغ</th>
                            <th className="px-4 py-2 text-right font-body">البيان</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.vouchers.map((voucher) => (
                            <tr key={voucher.id} className="border-b">
                              <td className="px-4 py-2 font-body">{voucher.voucher_number}</td>
                              <td className="px-4 py-2 font-body">
                                <span
                                  className={`px-2 py-1 rounded text-xs ${
                                    voucher.voucher_type === 'قبض'
                                      ? 'bg-green-100 text-green-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}
                                >
                                  {voucher.voucher_type}
                                </span>
                              </td>
                              <td className="px-4 py-2 font-body font-bold">
                                {formatCurrency(voucher.amount)}
                              </td>
                              <td className="px-4 py-2 font-body">{voucher.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 font-body">لا توجد سندات في هذه الفترة</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 font-body">لا توجد بيانات متاحة</p>
          </div>
        )}
      </main>
    </div>
  );
}
